import { useCallback, useEffect, useId, useState, type MouseEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const NAV_LINKS = [
  { label: "Resources", to: "/resources" },
  { label: "Newsletter", to: "/newsletter" },
  { label: "Upskill Your Team", to: "/business" },
  { label: "AI Directory", to: "/daily-ai-updates" },
] as const;

function joinTargetForPath(pathname: string): string {
  const p = pathname.replace(/\/$/, "") || "/";
  if (p.startsWith("/newsletter/")) return `${p}#homepage-cta`;
  if (p === "/newsletter") return "/newsletter#homepage-cta";
  if (p === "/resources") return "/resources#resource-cta";
  if (p === "/all-resources") return "/all-resources#all-resources-cta";
  if (p === "/business") return "/business#resource-cta";
  // Legacy LeadFunnelPage routes have a `#join` section anchor.
  if (
    p === "/ig" ||
    p === "/x" ||
    p === "/linkedin" ||
    p === "/yt" ||
    p === "/community" ||
    p === "/vk" ||
    p === "/codex" ||
    p === "/ai-roadmap" ||
    p === "/mastermind"
  ) {
    return `${p}#join`;
  }
  // Homepage (mounted at "/") + final fallback. v2 Homepage's bottom CTA
  // section has id="homepage-cta".
  return "/#homepage-cta";
}

function HamburgerIcon({ open }: { open: boolean }) {
  return (
    <span className="flex h-5 w-6 flex-col justify-center gap-1.5" aria-hidden>
      <span
        className={`h-0.5 w-full origin-center rounded-sm bg-white transition-transform ${open ? "translate-y-2 rotate-45" : ""}`}
      />
      <span
        className={`h-0.5 w-full rounded-sm bg-white transition-opacity ${open ? "opacity-0" : "opacity-100"}`}
      />
      <span
        className={`h-0.5 w-full origin-center rounded-sm bg-white transition-transform ${open ? "-translate-y-2 -rotate-45" : ""}`}
      />
    </span>
  );
}

export default function Navbar() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const menuId = useId();
  const closeMobile = useCallback(() => setMobileOpen(false), []);
  const joinTo = joinTargetForPath(pathname);

  const handleJoinNavigation = useCallback(
    (e: MouseEvent<HTMLAnchorElement>) => {
      const i = joinTo.indexOf("#");
      if (i === -1) return;
      const pathPart = joinTo.slice(0, i).replace(/\/$/, "") || "/";
      const hashPart = joinTo.slice(i + 1);
      if (!hashPart) return;
      const cur = pathname.replace(/\/$/, "") || "/";
      if (pathPart !== cur) return;
      e.preventDefault();
      closeMobile();
      void navigate({ pathname: pathPart, hash: hashPart }, { replace: true });
      window.setTimeout(() => {
        document.getElementById(hashPart)?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 0);
    },
    [joinTo, pathname, navigate, closeMobile],
  );

  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeMobile();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [mobileOpen, closeMobile]);

  return (
    <header
      className="sticky top-0 z-[110] w-full border-b border-[#9A9DA6]/35 bg-black/75 backdrop-blur-md md:border-b-0 md:bg-black md:backdrop-blur-none"
      style={{ fontFamily: "var(--font-fira)" }}
    >
      <div className="mx-auto flex w-[90%] max-w-full items-center justify-between py-3.5 md:grid md:w-[80%] md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] md:items-center md:py-4">
        <Link
          to="/"
          className="flex min-w-0 shrink-0 items-center gap-2.5 md:justify-self-start"
          onClick={closeMobile}
        >
          <img
            src="/homepage/salogo.svg"
            alt="Staying Ahead"
            className="h-8 w-auto sm:h-9"
            width={120}
            height={32}
            decoding="async"
          />
        </Link>

        <nav
          className="hidden items-center gap-7 lg:gap-10 md:flex md:justify-self-center"
          aria-label="Primary"
        >
          {NAV_LINKS.map(({ label, to }) => (
            <Link
              key={label}
              to={to}
              className="whitespace-nowrap text-sm text-white/65 transition hover:text-white lg:text-[15px]"
            >
              {label}
            </Link>
          ))}
        </nav>

        <div className="flex shrink-0 items-center justify-end gap-3 md:justify-self-end">
          <div className="hidden items-center gap-3 md:flex">
            <Link
              to="/invite-as-speaker"
              onClick={closeMobile}
              className="rounded-lg border border-[#FFFFFF4D] bg-transparent px-4 py-2 text-center text-sm font-medium text-white transition hover:bg-white/5 lg:px-5"
            >
              Invite as Speaker
            </Link>
            <Link
              to={joinTo}
              onClick={handleJoinNavigation}
              className="rounded-lg border border-[#FFFFFF4D] bg-white px-4 py-2 text-center text-sm font-semibold text-black transition-opacity hover:opacity-90 lg:px-5"
            >
              Join Now
            </Link>
          </div>

          <button
            type="button"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-white/20 text-white md:hidden"
            aria-expanded={mobileOpen}
            aria-controls={menuId}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            onClick={() => setMobileOpen((o) => !o)}
          >
            <HamburgerIcon open={mobileOpen} />
          </button>
        </div>
      </div>

      {mobileOpen ? (
        <div
          id={menuId}
          className="border-t border-[#9A9DA6]/35 bg-black/95 px-[5%] py-4 backdrop-blur-lg md:hidden"
          role="dialog"
          aria-modal="true"
          aria-label="Menu"
        >
          <nav className="flex flex-col gap-1" aria-label="Mobile primary">
            {NAV_LINKS.map(({ label, to }) => (
              <Link
                key={label}
                to={to}
                className="rounded-lg px-3 py-3 text-base text-white/80 transition hover:bg-white/5 hover:text-white"
                onClick={closeMobile}
              >
                {label}
              </Link>
            ))}
            <div className="mt-3 flex flex-row gap-2 sm:gap-3">
              <Link
                to="/invite-as-speaker"
                className="min-w-0 flex-1 rounded-lg border border-[#FFFFFF4D] bg-transparent px-2 py-3 text-center text-sm font-medium leading-snug text-white transition hover:bg-white/5 sm:px-3 sm:text-base"
                onClick={closeMobile}
              >
                Invite as Speaker
              </Link>
              <Link
                to={joinTo}
                className="min-w-0 flex-1 rounded-lg border border-[#FFFFFF4D] bg-white px-2 py-3 text-center text-sm font-semibold leading-snug text-black transition-opacity hover:opacity-90 sm:px-3 sm:text-base"
                onClick={(e) => {
                  handleJoinNavigation(e);
                  closeMobile();
                }}
              >
                Join Now
              </Link>
            </div>
          </nav>
        </div>
      ) : null}
    </header>
  );
}
